import {
  Button,
  Field,
  Input,
  Portal,
  Select,
  Stack,
  Text,
  createListCollection,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTransactions } from "../context/TransactionsContext";
import { Toaster, toaster } from "../components/ui/toaster";
import { useCustomCategories } from "../context/CustomCatgoriesContext";
import { SavingGoal } from "../context/SavingGoalsContext";
import CustomSelect from "./CustomSelect";

type SelectTypes = {
  label: string;
  value: string;
};

export const TransactionForm = () => {
  const { addTransaction } = useTransactions();
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [type, setType] = useState<"income" | "expense" | "saving">("income");
  const [category, setCategory] = useState("");
  const [transactionListDate, setTransactionListDate] = useState<string>("");
  const { categories: customCategories } = useCustomCategories();
  const [savingGoals, setSavingGoals] = useState<SavingGoal[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);

  useEffect(() => {
    const storedGoals = localStorage.getItem("savingGoals");
    if (storedGoals) {
      try {
        const parsedGoals = JSON.parse(storedGoals);
        setSavingGoals(parsedGoals);
      } catch (err) {
        console.error("Fehler beim Parsen der Sparziele:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedGoalId) {
      const goal = savingGoals.find((goal) => goal.id === selectedGoalId);
      if (goal) {
        setCategory(goal.category);
        setType("saving");
      }
    } else {
      setCategory("");
      setType("income");
    }
  }, [selectedGoalId, savingGoals]);

  const categoryCollection = createListCollection({
    items: customCategories.map((cat) => ({
      label: cat.label,
      value: cat.value,
    })),
  });

  const savingGoalSelectCollection = createListCollection({
    items: [
      { label: "Kein Sparziel ausgewählt...", value: "none" },
      ...savingGoals.map((goal) => ({
        label: goal.title
          ? `${goal.title} (${goal.category})`
          : `Kategorie: ${goal.category}`,
        value: goal.id,
      })),
    ],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const selectedGoal = savingGoals.find((goal) => goal.id === selectedGoalId);
    const finalCategory = selectedGoal ? selectedGoal.category : category;
    const finalDescription = selectedGoal
      ? selectedGoal.title || selectedGoal.category
      : description;

    const transactionDate = transactionListDate
      ? new Date(transactionListDate).toISOString()
      : new Date().toISOString();

    addTransaction({
      id: uuidv4(),
      description: finalDescription,
      amount: parseFloat(amount),
      type,
      date: new Date().toISOString(),
      category: finalCategory,
      transactionListDate: transactionDate,
      savingGoalId: selectedGoalId || "",
    });

    setDescription("");
    setAmount("");
    setTransactionListDate("");
    setSelectedGoalId(null);

    toaster.create({
      description: "Der Betrag wurde erfolgreich hinzugefügt",
      type: "success",
      placement: "top-end",
    });
  };

  const selectMoneyType = createListCollection({
    items: [
      { label: "Einnahmen", value: "income" },
      { label: "Ausgaben", value: "expense" },
      { label: "Sparen", value: "saving" },
    ],
  });

  return (
    <>
      <Toaster />
      <form onSubmit={handleSubmit}>
        <Stack spaceY={4} pb={4}>
          <Field.Root>
            <Field.Label>Betrag</Field.Label>
            <Input
              required
              type="number"
              inputMode="decimal"
              pattern="[0-9]*"
              borderColor="gray"
              placeholder="z.B. 40"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field.Root>
          {!selectedGoalId ? (
            <>
              <Field.Root>
                <Field.Label>Beschreibung</Field.Label>
                <Input
                  required={!selectedGoalId}
                  disabled={!!selectedGoalId}
                  placeholder="z.B. Einkauf"
                  value={description}
                  borderColor="gray"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field.Root>

              <Field.Root>
                <Field.Label>Kategorie</Field.Label>
                <Select.Root
                  width={"100%"}
                  size="sm"
                  collection={categoryCollection}
                  onChange={(e: any) => setCategory(e.target.value)}
                  required
                  /* value={[category]} */
                  disabled={!!selectedGoalId}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger borderColor="gray">
                      <Select.ValueText placeholder="Kategorie wählen" />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Portal>
                    <Select.Positioner>
                      <Select.Content>
                        {categoryCollection.items.map((item: SelectTypes) => (
                          <Select.Item item={item} key={item.value}>
                            {item.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Portal>
                </Select.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>Einnahmen/Ausgaben/Sparen</Field.Label>
                <Select.Root
                  collection={selectMoneyType}
                  size="sm"
                  required
                  width="100%"
                  onChange={(e: any) =>
                    setType(e.target.value as "income" | "expense" | "saving")
                  }
                  /*  value={[type]} */
                  disabled={!!selectedGoalId}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger borderColor="gray" value={type}>
                      <Select.ValueText placeholder="Wähle einen Typ" />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Portal>
                    <Select.Positioner>
                      <Select.Content>
                        {selectMoneyType.items.map((type: SelectTypes) => (
                          <Select.Item item={type} key={type.value}>
                            {type.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Portal>
                </Select.Root>
              </Field.Root>
            </>
          ) : null}
          {savingGoals.length > 0 && (
            <Field.Root>
              <Field.Label>Sparziel auswählen (optional)</Field.Label>
              <CustomSelect
                options={savingGoalSelectCollection.items}
                placeholder="Sparziel auswählen"
                value={selectedGoalId ?? "none"}
                onChange={(value: string) => {
                  setSelectedGoalId(value === "none" ? null : value);
                }}
              />
            </Field.Root>
          )}
          <Field.Root>
            <Field.Label>Datum</Field.Label>
            <Text color="gray.500" fontSize="smaller">
              Wird kein Wert angegeben, wird das aktuelle Datum genutzt
            </Text>
            <Input
              type="date"
              placeholder="Datum"
              borderColor="gray"
              value={transactionListDate}
              onChange={(e) => setTransactionListDate(e.target.value)}
            />
          </Field.Root>

          <Button colorScheme="teal" type="submit">
            Hinzufügen
          </Button>
        </Stack>
      </form>
    </>
  );
};
